import { datadogRum } from '@datadog/browser-rum';
import { ErrorComponent, ErrorComponentProps } from '@tanstack/react-router';
import useTexts from 'common/components/errorBoundary/hooks/useTexts';
import MessagePrompt from 'common/components/messagePrompt';
import { FC } from 'react';
import { isLocalHost } from 'services/localhost';
import { FEATURE_FLAGS_KEYS } from '../../../constants/featureFlags';
import { useFeatureFlagQuery } from '../../../remote-state/featureFlagsHooks';

export const RouterErrorDisplay: FC<ErrorComponentProps> = ({ error, reset }) => {
  const { title, btnOkText, mainText } = useTexts();
  const { data: isErrorBoundUiEnabled } = useFeatureFlagQuery({
    flagKey: FEATURE_FLAGS_KEYS.ERROR_BOUNDARY_UI_ENABLED,
    defaultValue: false,
  });

  if (isLocalHost()) {
    console.error('Error caught by TanStack Router:', error);
  } else {
    datadogRum.addError(error, {
      info: 'Unexpected error caught in ROUTER error boundary',
    });
  }

  if (process.env.NODE_ENV === 'development') {
    return (
      <pre
        style={{
          marginTop: '10px',
          textAlign: 'left',
          background: '#f0f0f0',
          padding: '10px',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
        }}
      >
        {error.message}
        {error.stack}
      </pre>
    );
  }

  if (!isErrorBoundUiEnabled) {
    return <ErrorComponent error={error} />;
  }

  return (
    <MessagePrompt
      open
      title={title ?? 'Something went wrong in the application'}
      btnOkText={btnOkText ?? 'Refresh Page'}
      onOkClick={() => window.location.reload()}
      onClose={() => {
        if (reset) {
          reset();
        } else {
          window.location.reload();
        }
      }}
    >
      {mainText ?? 'An unexpected error occurred. Try refreshing the page or contact support if the issue persists.'}
    </MessagePrompt>
  );
};
